import { AlertCircle, Paperclip, Send, Square, X } from "lucide-react";
import React, {
  useRef,
  useState,
  type FormEvent,
  type PointerEvent,
} from "react";
import "../chat-v8.css";

import type {
  T_ChatV8RunMode,
  T_ChatV8RunModeOption,
} from "@lattice/core-lib/types/chat-v8";
import type { T_ChatV8StagedAttachment } from "@lattice/shared-frontend/chat-v8";
import type { T_ShellChatSurface } from "@lattice/shared-frontend/shell";

import { IconButton } from "@lattice/frontend/components/kit/IconButton";
import { Pill } from "@lattice/frontend/components/kit/Pill";
import { TextField } from "@lattice/frontend/components/kit/TextField";

import { ComposerFocusSuggestions } from "./ComposerFocusSuggestions";
import { ModelPickerPill } from "./ModelPickerPill";

export type T_ChatComposerAttachments = {
  accept: string;
  canSend: boolean;
  onRemove: (localId: string) => void;
  onRetry: (localId: string) => void;
  onStageFiles: (files: readonly File[]) => void;
  staged: readonly T_ChatV8StagedAttachment[];
};

export type T_ChatComposerProps = {
  attachments?: T_ChatComposerAttachments;
  availableRunModes?: readonly T_ChatV8RunModeOption[];
  disabled: boolean;
  focused?: boolean;
  onFocusChange?: (focused: boolean) => void;
  onFocusPrepare?: () => void;
  onInputElementChange?: (
    element: HTMLInputElement | HTMLTextAreaElement | null,
  ) => void;
  onRunModeChange?: (mode: T_ChatV8RunMode) => void;
  onSend: (message: string) => Promise<void>;
  prefilledMessage?: string;
  runMode?: T_ChatV8RunMode;
  /** When set, a run is in flight: the send button becomes a stop button. */
  onCancelRun?: () => void;
  suggestionSurface?: T_ShellChatSurface;
  usageWarningPercent?: number;
};

function StagedAttachmentChip({
  attachment,
  disabled,
  onRemove,
  onRetry,
}: {
  attachment: T_ChatV8StagedAttachment;
  disabled: boolean;
  onRemove: () => void;
  onRetry: () => void;
}) {
  return (
    <span className="chat-v8-attachment-chip" data-status={attachment.status}>
      {attachment.status === "uploading" ? (
        <span aria-hidden className="lk-spinner lk-spinner--small" />
      ) : null}
      {attachment.status === "failed" ? (
        <AlertCircle aria-hidden size={13} />
      ) : null}
      <span className="chat-v8-attachment-chip__name">{attachment.name}</span>
      {attachment.status === "failed" ? (
        <button
          className="chat-v8-attachment-chip__action"
          disabled={disabled}
          onClick={onRetry}
          type="button"
        >
          Retry
        </button>
      ) : null}
      <button
        aria-label={`Remove ${attachment.name}`}
        className="chat-v8-attachment-chip__remove"
        disabled={disabled}
        onClick={onRemove}
        type="button"
      >
        <X aria-hidden size={13} />
      </button>
    </span>
  );
}

export function ChatComposer({
  attachments,
  availableRunModes = [],
  disabled,
  focused = false,
  onFocusChange,
  onFocusPrepare,
  onInputElementChange,
  onCancelRun,
  onRunModeChange,
  onSend,
  prefilledMessage = "",
  runMode,
  suggestionSurface,
  usageWarningPercent,
}: T_ChatComposerProps) {
  const [message, setMessage] = useState(prefilledMessage);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const inputElementRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(
    null,
  );
  const attachmentsReady = attachments?.canSend ?? true;
  const hasStagedUploads = (attachments?.staged.length ?? 0) > 0;
  const canSubmit =
    !disabled &&
    attachmentsReady &&
    (message.trim().length > 0 || hasStagedUploads);
  // Typed text filters the list rather than hiding it (the component
  // renders nothing when no prompt matches). Staged uploads still suppress
  // suggestions entirely: a canned question must never carry an attachment
  // the user staged for their own message.
  const showFocusSuggestions =
    focused && !hasStagedUploads && suggestionSurface !== undefined;
  const submitMessage = (messageToSend: string) => {
    const trimmed = messageToSend.trim();
    // Validates the submitted text, not the input state: suggestion presses
    // send their own prompt while the input is empty.
    if (disabled || !attachmentsReady || !trimmed) return;
    setMessage("");
    inputElementRef.current?.blur();
    void onSend(trimmed);
  };
  const submit = (event: FormEvent) => {
    event.preventDefault();
    submitMessage(message);
  };
  return (
    <>
      {showFocusSuggestions ? (
        <ComposerFocusSuggestions
          chatSurface={suggestionSurface}
          filterQuery={message}
          onPress={submitMessage}
        />
      ) : null}
      <form
        aria-label="Message Prism"
        className="chat-v8-composer"
        onPointerDownCapture={(event: PointerEvent<HTMLFormElement>) => {
          if (event.target === inputElementRef.current) onFocusPrepare?.();
        }}
        onSubmit={submit}
      >
        {attachments && attachments.staged.length > 0 ? (
          <div className="chat-v8-attachment-row">
            {attachments.staged.map((stagedAttachment) => (
              <StagedAttachmentChip
                attachment={stagedAttachment}
                disabled={disabled}
                key={stagedAttachment.localId}
                onRemove={() => attachments.onRemove(stagedAttachment.localId)}
                onRetry={() => attachments.onRetry(stagedAttachment.localId)}
              />
            ))}
          </div>
        ) : null}
        <TextField
          containerStyle={{ width: "100%" }}
          disabled={disabled}
          embedded
          multiline
          inputRef={(element) => {
            inputElementRef.current = element;
            onInputElementChange?.(element);
          }}
          onBlur={() => onFocusChange?.(false)}
          onChange={(event) => setMessage(event.target.value)}
          onFocus={() => onFocusChange?.(true)}
          onKeyDown={(event) => {
            // The multiline textarea swallows Enter as a newline; on web,
            // Enter sends and Shift+Enter inserts the newline. Skip while
            // composing (IME) so committing CJK text does not send.
            if (
              event.key === "Enter" &&
              !event.shiftKey &&
              !event.nativeEvent.isComposing
            ) {
              event.preventDefault();
              submitMessage(message);
            }
          }}
          placeholder="Ask Prism anything…"
          rows={1}
          value={message}
        />
        <div className="chat-v8-composer__actions">
          {attachments ? (
            <>
              <input
                accept={attachments.accept}
                className="chat-v8-attachment-input"
                multiple
                onChange={(event) => {
                  attachments.onStageFiles([...(event.target.files ?? [])]);
                  event.target.value = "";
                }}
                ref={fileInputRef}
                type="file"
              />
              <IconButton
                ariaLabel="Attach images"
                disabled={disabled}
                icon={<Paperclip aria-hidden size={16} />}
                onClick={() => fileInputRef.current?.click()}
                size="compact"
                variant="quiet"
              />
            </>
          ) : null}
          {runMode !== undefined && onRunModeChange !== undefined ? (
            <ModelPickerPill
              modes={availableRunModes}
              onChange={onRunModeChange}
              value={runMode}
            />
          ) : null}
          <span aria-hidden className="chat-v8-composer__actions-spacer" />
          {usageWarningPercent !== undefined ? (
            <Pill label={`${usageWarningPercent}% used`} tone="accent" />
          ) : null}
          {onCancelRun !== undefined ? (
            <IconButton
              ariaLabel="Stop response"
              icon={<Square aria-hidden fill="currentColor" size={14} />}
              onClick={onCancelRun}
              size="compact"
            />
          ) : (
            <IconButton
              ariaLabel="Send message"
              disabled={!canSubmit || message.trim().length === 0}
              icon={<Send aria-hidden size={18} />}
              onClick={() => submitMessage(message)}
              size="compact"
            />
          )}
        </div>
      </form>
    </>
  );
}
